// posthog-service.ts
import posthog from "posthog-js";
import { TrackerPageViewData, TrackerService, TrackerServiceProps } from "./tracker-service";
import { takeId } from "shared";
import { ENVIRONMENT, POSTHOG_HOST, POSTHOG_KEY } from "../../config";

let isPostHogInitialized = false;

type IdentitySnapshot = {
	platform: string;
	workspace?: string;
	room?: string;
	user?: string;
};

/**
 * Call once on app bootstrap.
 */
function initPostHog(host: string = POSTHOG_HOST, key: string = POSTHOG_KEY) {
	if (typeof window === "undefined") return false;
	if (!key) return false;
	if (isPostHogInitialized) return true;

	posthog.init(key, {
		api_host: host,
		person_profiles: "identified_only",
		capture_pageview: false, // We manage pageviews manually
		capture_pageleave: false,
		autocapture: true,
		rageclick: true,
		capture_dead_clicks: true,
		capture_heatmaps: true,
		capture_exceptions: true,
		capture_performance: true,
		advanced_disable_flags: true,
		advanced_disable_feature_flags: true,
		disable_surveys: true,
		disable_surveys_automatic_display: true,
		disable_web_experiments: true,
		__preview_deferred_init_extensions: true,
		disable_session_recording: true,
		session_recording: {
			maskAllInputs: true,
			maskTextSelector: "*",
			strictMinimumDuration: true
		}
	});

	posthog.register({ environment: ENVIRONMENT });
	isPostHogInitialized = true;

	// Activate session recording for a subset of users based on the sampling rate (set it in their settings UI).
	posthog.startSessionRecording({ sampling: true });

	return true;
}

export class PostHogService implements TrackerService {
	private props: TrackerServiceProps = {};
	private isInitialized = false;

	setup(props: TrackerServiceProps): Promise<void> | void {
		this.props = { ...this.props, ...props };
		this.isInitialized = initPostHog();
		this.ensureIdentity(this.props);
	}

	setProps(props: TrackerServiceProps): Promise<void> | void {
		this.props = { ...this.props, ...props };
		this.ensureIdentity(this.props);
	}

	private getPropsData(props?: TrackerServiceProps) {
		const { user, workspace, room } = props || {};
		return {
			workspace: workspace != null ? takeId(workspace) : undefined,
			room: room != null ? takeId(room) : undefined,
			user: user != null ? takeId(user) : undefined
		};
	}

	private getIdentitySnapshot(props?: TrackerServiceProps): IdentitySnapshot {
		const platform = props?.platform || "webapp";
		return {
			platform,
			...this.getPropsData(props)
		};
	}

	private ensureIdentity(props?: TrackerServiceProps) {
		if (typeof window === "undefined") return;
		if (!this.isInitialized) return;

		const snapshot = this.getIdentitySnapshot(props);

		if (snapshot.user) {
			posthog.identify(snapshot.user);
		}

		if (snapshot.workspace) {
			posthog.group("workspace", snapshot.workspace);
		}

		posthog.register(snapshot);
	}

	async trackPageView({ data, props }: { data?: TrackerPageViewData; props?: TrackerServiceProps }) {
		return this.track({ name: "$pageview", baseData: data, props });
	}

	async trackEvent(
		name: string,
		{ data, props, pageData }: { data?: object; props?: TrackerServiceProps; pageData?: TrackerPageViewData }
	) {
		const baseData = pageData;
		return this.track({ name, baseData, eventData: data, props });
	}

	private async track({
		name,
		baseData,
		eventData,
		props
	}: { baseData?: object; name?: string; eventData?: any; props?: TrackerServiceProps } = {}) {
		const activeProps = props ?? this.props;
		this.ensureIdentity(activeProps);

		const platform = activeProps?.platform || "webapp";
		const ids = this.getPropsData(activeProps);

		const eventName = name ?? "$pageview";
		const properties = {
			...(baseData ?? {}),
			...(eventData ?? {}),
			platform,
			...ids
		};

		posthog.capture(eventName, properties);
	}
}

export const posthogService = new PostHogService();
